import React, { useState, useEffect } from "react";
import { signInWithEmailAndPassword, sendPasswordResetEmail, updateProfile } from "firebase/auth"; // Import sendPasswordResetEmail
import { auth, db } from "./firebase-config"; // Your Firebase config
import { Link, useLocation, useNavigate } from "react-router-dom";
import { doc, getDoc, updateDoc, serverTimestamp } from "firebase/firestore"; // Import updateDoc and serverTimestamp
import { ColorRing } from "react-loader-spinner";
import { useTranslation } from 'react-i18next';
import myLogo from "./public/ProRecruitBusinessLogoTransparent2.png";


function SignInPage() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [resetEmailSent, setResetEmailSent] = useState(false); // Track if reset email was sent
    const navigate = useNavigate();
    const location = useLocation(); // <--- Get location object
    const { t } = useTranslation("SignIn");  //  Correct the "namespace"


    // Determine the path to redirect to after successful login
    // If location.state?.from exists, use its pathname. Otherwise, default (will be decided later based on loginCount)
    const redirectPath = location.state?.from?.pathname || null; // Store potential redirect path

    useEffect(() => {
        document.title = t('signInPageTitle') + " - ProRecruit";
        return () => {
            document.title = "ProRecruit";
        };
    }, [t]);

    const handleSignIn = async (e) => {
        e.preventDefault();
        setError(null);
        setLoading(true);
        setResetEmailSent(false); // Clear reset message on new sign-in attempt

        try {
            // 1. Sign in the user
            const userCredential = await signInWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            // 2. Check email verification
            if (!user.emailVerified) {
                await auth.signOut();
                setError(t("emailNotVerified")); // Use i18next
                return;
            }

            // 3. Fetch user data (including loginTime)
            const userDocRef = doc(db, 'users', user.email);
            const userDocSnap = await getDoc(userDocRef);

            if (!userDocSnap.exists()) {
                throw new Error("User data not found in Firestore."); // More specific error
            }
            const userData = userDocSnap.data().userData;

            if (!user.displayName) {
                updateProfile(user, { displayName: userData.displayName });
            }
            //check if user custom claim exist if not send to api to create
            const idTokenResult = await user.getIdTokenResult();
            if (!idTokenResult.claims.tenantId) {
                const idToken = await user.getIdToken();
                const claimsResponse = await fetch(
                    `${process.env.REACT_APP_FIREBASE_SET_CUSTOM_CLAIMS_URL}/setCustomClaims`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${idToken}`,
                        },
                        body: JSON.stringify({
                            uid: user.uid,
                            audience: 'recruiter',
                            language: userData.language,
                            tenantId: userData.tenantId,
                            canUpdateCompany: userData.canUpdateCompany,
                            canCreateAssessment: userData.canCreateAssessment,
                            canAddJobOffer: userData.canAddJobOffer,
                            hasPurchasedLicense: userData.hasPurchasedLicense,
                            originalTenantId: process.env.REACT_APP_DEFAULT_TENANT_ID,
                            signupSource: "web_form",
                        }),
                    }
                );
                if (!claimsResponse.ok) {
                    const errorData = await claimsResponse.json();
                    console.error(
                        "Error setting custom claims:",
                        claimsResponse.status,
                        errorData
                    );
                } else {
                    const successData = await claimsResponse.json();
                    console.log("Custom claims set successfully:", successData);
                }
            }

            // 4. Update login count and last login
            let loginCount = userData.loginCount || 0; // Default to 0 if it doesn't exist

            // Create the update object. Only update loginTime if <= 3.
            const updateData = {
                'userData.lastLogin': serverTimestamp() // ALWAYS update lastLogin
            };

            let navigateToGettingStarted = false;
            if (loginCount <= 3) {
                loginCount++;
                updateData['userData.loginCount'] = loginCount;
                navigateToGettingStarted = true; // Flag to navigate to Getting Started
            }
            await updateDoc(userDocRef, updateData);


            // 5. Navigate after successful login
            if (navigateToGettingStarted) {
                 // Force navigation to Getting Started for the first few logins
                 console.log(`Login count (${loginCount}) <= 3, navigating to Getting Started.`);
                 navigate("/GettingStartedGuide", { state: { shouldSeeWelcomePopup: true }, replace: true });
            } else if (redirectPath) {
                 // If not first few logins AND we have a redirectPath, go there
                 console.log(`Login count > 3, navigating back to original path: ${redirectPath}`);
                 navigate(redirectPath != "signup" ? redirectPath : "/dashboard", { replace: true });
            } else {
                 // If not first few logins and NO redirectPath (e.g., navigated directly to signin), go to dashboard
                 console.log(`Login count > 3, no specific redirect path, navigating to dashboard.`);
                 navigate("/dashboard", { replace: true });
            }

        } catch (error) {
            console.error("Sign in error:", error);

            // Provide more specific error messages based on the error code.
            let errorMessage = t('signInError'); // Default error message
            if (error.code === 'auth/invalid-email') {
                errorMessage = t('invalidEmail');
            } else if (error.code === 'auth/user-disabled') {
                errorMessage = t('userDisabled');
            } else if (error.code === 'auth/user-not-found') {
                errorMessage = t('userNotFound');
            } else if (error.code === 'auth/wrong-password') {
                errorMessage = t('wrongPassword');
            }
            setError(errorMessage);

        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async () => {
        if (!email) {
            setError(t("enterEmailForReset")); // Use i18next
            return;
        }

        setLoading(true); // Start loading (optional, but good UX)
        setError(null);     // Clear any previous errors

        try {
            await sendPasswordResetEmail(auth, email);
            setResetEmailSent(true);
            setError(null); // Clear error if email sent successfully
        } catch (error) {
            console.error("Password reset error:", error);
            let errorMessage = t("passwordResetError"); // Default error message.
            if (error.code === 'auth/invalid-email') {
              errorMessage = t('invalidEmail');  // Re-use the invalid email message.
            } else if (error.code === 'auth/user-not-found') {
              errorMessage = t('userNotFound'); // Re-use user-not-found.
            }
            setError(errorMessage);

        } finally {
            setLoading(false); // Stop loading
        }
    };


    return (
        <div className="min-h-screen flex items-center justify-center bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-slate-200/50 p-8 space-y-8 transition-all duration-300">
                <div>
                <img
                    className="mx-auto h-12 w-auto"
                    src={myLogo}  // Replace with your logo path
                    alt="ProRecruit"
                />
                    <h2 className="mt-6 text-center text-3xl font-extrabold text-slate-900 font-bold tracking-tight">
                        {t("signInTitle")}
                    </h2>
                    <p className="mt-2 text-center text-sm text-slate-600 leading-relaxed">
                        {t("or")} {' '}
                        <Link to="/signup" className="font-medium text-indigo-600 hover:text-indigo-500">
                            {t("createAccount")}
                        </Link>
                    </p>
                    {error && (
                        <p className="mt-2 text-center text-sm text-red-600">{error}</p>
                    )}
                    {/* Success message for password reset */}
                    {resetEmailSent && (
                      <p className="mt-2 text-center text-sm text-green-600">{t("passwordResetSent")}</p>
                    )}
                </div>
                <form className="mt-8 space-y-6" onSubmit={handleSignIn}>
                    <input type="hidden" name="remember" value="true" />
                    <div className="rounded-md shadow-sm -space-y-px">
                        <div>
                            <label htmlFor="email-address" className="sr-only">{t("email")}</label>
                            <input
                                id="email-address"
                                name="email"
                                type="email"
                                autoComplete="email"
                                required
                                className="appearance-none rounded-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-slate-900 font-semibold tracking-tight rounded-t-xl focus:outline-none focus:ring-[#A47713] focus:border-[#A47713] focus:z-10 sm:text-sm transition-all"
                                placeholder={t("emailPlaceholder")}
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                        </div>
                        <div>
                            <label htmlFor="password" className="sr-only">{t("password")}</label>
                            <input
                                id="password"
                                name="password"
                                type="password"
                                autoComplete="current-password"
                                required
                                className="appearance-none rounded-none relative block w-full px-3 py-3 border border-gray-300 placeholder-gray-500 text-slate-900 font-semibold tracking-tight rounded-b-xl focus:outline-none focus:ring-[#A47713] focus:border-[#A47713] focus:z-10 sm:text-sm transition-all"
                                placeholder={t("passwordPlaceholder")}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </div>
                    </div>

                    {/* Forgot Password Link */}
                    <div className="flex items-center justify-between">
                        <div className="text-sm">
                            <button
                                type="button"  //  Important:  Make this button type="button"
                                className="font-medium text-indigo-600 hover:text-indigo-500"
                                onClick={handleForgotPassword}
                            >
                                {t("forgotPassword")}
                            </button>
                        </div>
                    </div>

                    <div>
                        <button
                            type="submit"
                            disabled={loading}
                            className="group relative w-full flex justify-center py-3 px-8 border border-transparent text-sm font-semibold rounded-full text-white bg-[#A47713] hover:bg-[#8a6310] shadow-xl hover:scale-105 hover:-translate-y-1 hover:shadow-2xl transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#A47713]"
                        >
                        {loading ? (
                        <ColorRing
                        visible={true}
                        height="20"
                        width="20"
                        ariaLabel="color-ring-loading"
                        wrapperStyle={{}}
                        wrapperClass="color-ring-wrapper"
                        colors={[
                        "#e15b64",
                        "#f47e60",
                        "#f8b26a",
                        "#abbd81",
                        "#849b87",
                        ]}
                    />
                    ) : (
                    t("signInButton")
                    )}
                        </button>
                    </div>
                </form>
                    <div className="text-center">
                        <Link to="/signup">
                        <button
                            type="button"
                            className="rounded-md px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#A47713]"
                        >
                            {t("signUpButton")}
                        </button>
                        </Link>
                    </div>
            </div>
        </div>
    );
}

export default SignInPage;